/**
 * A room's style, read off its surfaces' own answers (2026-10-11).
 *
 * The existing-conditions pass judges style per SURFACE -- an era, a named look, current /
 * neutral / dated, and the cues behind it -- because a room is often half updated: new
 * subway tile in the shower beside 1970s harvest-gold tile behind the sink. So the room's
 * summary lists the surfaces under each verdict instead of deciding one word for the room;
 * the counts are there for a headline. Computed on read, never stored, so it always follows
 * the surfaces.
 *
 * Rooms are grouped by `roomId`: a room split off another room's capture shares that
 * capture's `roomIndex` (a kitchen and the bathroom carved out of one scan room).
 */

const { STYLE_VERDICTS } = require('./vocabulary');

const surfaceWord = (doc) =>
  doc.kind === 'wall-face'
    ? 'wall'
    : doc.kind === 'object'
      ? doc.anchor?.type || 'fixture'
      : doc.kind;

/** One surface's style as the model judged it, or null when it gave none. */
function surfaceStyle(doc) {
  const s = doc.style;
  if (doc.verdict !== 'observed' || !s || !STYLE_VERDICTS.includes(s.verdict))
    return null;
  return {
    targetId: doc.targetId,
    surface: surfaceWord(doc),
    verdict: s.verdict,
    era: s.era ?? null,
    look: s.look ?? null,
    confidence: s.confidence ?? null,
    note: s.note ?? null,
  };
}

/** "four inch square tile wall, 1960s-70s": the look, what it is on, and the era. */
function styleLabel(s) {
  const look =
    s.look && s.look !== 'other' ? `${s.look.replace(/-/g, ' ')} ` : '';
  const era = s.era && s.era !== 'unknown' ? `, ${s.era}` : '';
  return `${look}${s.surface}${era}`;
}

/**
 * Every room among `docs` (existing-conditions documents), in the order they first appear:
 * `[{ roomId, roomIndex, roomName, dated, neutral, current, judged, unjudged }]`, each
 * verdict a list of `surfaceStyle`s; `unjudged` counts the surfaces with no style answer
 * (not seen, or seen without one).
 */
function roomStyles(docs) {
  const rooms = new Map();
  for (const d of docs || []) {
    const key = d.roomId ?? `index-${d.roomIndex}`;
    if (!rooms.has(key)) {
      rooms.set(key, {
        roomId: d.roomId ?? null,
        roomIndex: d.roomIndex ?? null,
        roomName: d.roomName ?? null,
        dated: [],
        neutral: [],
        current: [],
        judged: 0,
        unjudged: 0,
      });
    }
    const room = rooms.get(key);
    const s = surfaceStyle(d);
    if (s) {
      room[s.verdict].push(s);
      room.judged += 1;
    } else {
      room.unjudged += 1;
    }
  }
  return [...rooms.values()];
}

module.exports = { roomStyles, surfaceStyle, styleLabel };
